import React, { useState, useEffect } from "react";
import axios from "../../../configApi/axiosConfig";
import { APIURL } from "../../../configApi/apiConfig";

export const EstadoCuentaOperacion = ({ idCompra, isInline = false }) => {
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(null);

    useEffect(() => {
        const storedToken = localStorage.getItem("token");
        setToken(storedToken);
    }, []);

    useEffect(() => {
        if (token && idCompra) {
            fetchData();
        }
    }, [token, idCompra]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const url = APIURL.getViewEstadoCuentaOperacion();
            const response = await axios.post(url, { idCompra }, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            setPagos(response.data || []);
        } catch (error) {
            console.error("Error al cargar estado de cuenta de operacion:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="relative w-12 h-12">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-400 rounded-full opacity-20 animate-pulse"></div>
                    <div className="absolute inset-2 border-4 border-transparent border-t-green-600 border-r-green-500 rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    const COLUMNAS = [
        { label: '#', key: 'NumeroCuota', align: 'left', className: 'font-medium' },
        { label: 'Lote', key: 'NumeroLote', align: 'left' },
        { label: 'Vence', key: 'Vence', align: 'left', format: 'date' },
        { label: 'Tipo', key: 'Tipo', align: 'left' },
        { label: 'Doc', key: 'Doc', align: 'left' },
        { label: 'Tipo\nDe Pago', key: 'Tipo_Pago', align: 'left' },
        { label: 'Local', key: 'Local', align: 'left' },
        { label: 'Num\nAbono', key: 'Num_Abono', align: 'left' },
        { label: 'Fec\nAbono', key: 'Fecha_Abono', align: 'left', format: 'date' },
        { label: 'Total\nAbono', key: 'Total_Abono', align: 'right', format: 'currency', className: 'font-semibold' },
        { label: 'Fec\nCruzado', key: 'Fecha_Cruzado', align: 'left', format: 'date' },
        { label: 'Ab\nCuota', key: 'Abono_Cuota', align: 'right', format: 'currency' },
        { label: 'Ab\nGastos', key: 'Ab_GastosCuota', align: 'right', format: 'currency' },
        { label: 'Ab\nMora', key: 'Ab_MoraCuota', align: 'right', format: 'currency' },
        { label: 'Ab\nInteres', key: 'Ab_InteresCuota', align: 'right', format: 'currency' },
        { label: 'Ab\nCapital', key: 'Ab_CapitalCuota', align: 'right', format: 'currency' },
        { label: 'Fec\nEnvio', key: 'FechaEnvio', align: 'left', format: 'date' },
        { label: 'Estado\nCuota', key: 'Estado_Cuota', align: 'center' },
        { label: 'Fecha\nSistema', key: 'FechaSistema', align: 'left', format: 'date' },
        { label: 'Usuario', key: 'Usuario', align: 'left' }
    ];

    const formatValue = (value, format) => {
        if (format === 'date') return value ? new Date(value).toLocaleDateString('es-EC') : '';
        if (format === 'currency') return `$${parseFloat(value || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
        return value;
    };

    const getEstadoDot = (estado) => {
        if (!estado) return null;
        const e = estado.toUpperCase();
        let colorClass = 'bg-slate-500';
        let pulseClass = '';

        if (e === 'CANCELADO') {
            colorClass = 'bg-green-500';
        } else if (e === 'ABONADO') {
            colorClass = 'bg-amber-500';
            pulseClass = 'animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.6)]';
        } else if (e === 'ANULADO') {
            colorClass = 'bg-red-500';
        }

        return (
            <div className="flex justify-center" title={estado}>
                <span className={`w-3 h-3 rounded-full shadow-sm ${colorClass} ${pulseClass}`}></span>
            </div>
        );
    };

    const content = (
        <div className={`bg-white shadow-2xl p-0 space-y-0 ${!isInline ? 'rounded-3xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col' : 'w-full flex flex-col'} border border-slate-100`}>
            <div className="relative bg-gradient-to-r from-slate-900 via-green-900 to-slate-900 px-6 py-5 border-b border-slate-800 flex flex-col gap-4 overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-green-400 rounded-full mix-blend-screen filter blur-3xl"></div>
                </div>
                <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-1.5 h-7 bg-gradient-to-b from-green-400 to-green-500 rounded-full shadow-[0_0_10px_rgba(74,222,128,0.5)]"></div>
                        <h2 className="text-white text-lg font-bold tracking-wide flex items-center gap-2">
                            Estado de Cuenta <span className="px-3 py-1 bg-green-500/20 border border-green-400/30 rounded-lg text-green-300 text-sm tracking-widest shadow-inner">#{idCompra}</span>
                        </h2>
                    </div>

                    {/* Leyenda de Estados */}
                    <div className="flex items-center bg-slate-900/40 px-4 py-2 rounded-xl border border-slate-700/50 backdrop-blur-sm">
                        <span className="text-slate-400 text-[10px] font-bold tracking-wider uppercase mr-4 pr-4 border-r border-slate-700/80">Estado Cuota</span>
                        <div className="flex items-center gap-5">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm"></span>
                                <span className="text-slate-300 text-[11px] font-medium tracking-wide uppercase">Cancelado</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.5)]"></span>
                                <span className="text-slate-300 text-[11px] font-medium tracking-wide uppercase">Abonado</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm"></span>
                                <span className="text-slate-300 text-[11px] font-medium tracking-wide uppercase">Anulado</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-500 shadow-sm"></span>
                                <span className="text-slate-300 text-[11px] font-medium tracking-wide uppercase">Otro</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-x-auto overflow-y-auto bg-slate-50/50">
                <table className="w-full border-collapse text-[11px]">
                    <thead className="sticky top-0 z-20">
                        <tr className="bg-white shadow-[0_4px_20px_-10px_rgba(0,0,0,0.1)]">
                            {COLUMNAS.map(col => (
                                <th key={col.key} className={`px-4 py-3 text-${col.align} font-bold text-[10px] uppercase tracking-[0.1em] text-slate-500 border-b-2 border-slate-200 bg-white/90 backdrop-blur-sm ${col.label.includes('\n') ? 'whitespace-normal min-w-[70px]' : 'whitespace-nowrap'}`}>
                                    {col.label.split('\n').map((line, i, arr) => (
                                        <React.Fragment key={i}>
                                            {line}
                                            {i < arr.length - 1 && <br />}
                                        </React.Fragment>
                                    ))}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {pagos.map((item, index) => (
                            <tr key={index} className="bg-white hover:bg-green-50/60 transition-all duration-300 hover:shadow-[inset_4px_0_0_0_#22c55e] group">
                                {COLUMNAS.map(col => (
                                    <td key={col.key} className={`px-4 py-2 text-${col.align} ${col.className || 'text-slate-600'} whitespace-nowrap group-hover:text-slate-900 transition-colors`}>
                                        {col.key === 'Estado_Cuota' ? getEstadoDot(item[col.key]) : formatValue(item[col.key], col.format)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {pagos.length === 0 && (
                            <tr>
                                <td colSpan={COLUMNAS.length} className="px-4 py-16">
                                    <div className="flex flex-col items-center justify-center text-slate-400">
                                        <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 flex items-center justify-center border border-slate-200 border-dashed">
                                            <span className="text-2xl">📋</span>
                                        </div>
                                        <p className="font-semibold text-slate-500 text-sm tracking-wide">No hay registros</p>
                                        <p className="text-xs mt-1">El estado de cuenta para esta operación está vacío.</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return isInline ? content : (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900/60 via-green-900/30 to-slate-900/60 flex items-center justify-center z-50 p-4 backdrop-blur-md">
            {content}
        </div>
    );
};
